import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { BACKEND_URL } from '../../Constants'; // Adjust path if necessary

const QuizScoresList = () => {
    const [quizScores, setQuizScores] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchAllQuizScores = async () => {
            setLoading(true);
            setError(null);
            try {
                // Fetch scores from the enrollments/all-quiz-scores endpoint
                const enrollmentsResponse = await axios.get(`${BACKEND_URL}/api/enrollments/all-quiz-scores`);
                const enrollmentScores = enrollmentsResponse.data;

                // Fetch scores from the quiz-progress/all endpoint (chatbot quizzes)
                const chatbotProgressResponse = await axios.get(`${BACKEND_URL}/api/quiz-progress/all`);
                const chatbotScores = chatbotProgressResponse.data;

                // Combine both sets of scores
                // Assuming both endpoints return data in a compatible format
                const combinedScores = [...enrollmentScores, ...chatbotScores];

                // Sort the combined scores by date taken, newest first (optional, but good for display)
                combinedScores.sort((a, b) => new Date(b.dateTaken) - new Date(a.dateTaken));

                setQuizScores(combinedScores);
            } catch (err) {
                console.error('Error fetching combined quiz scores:', err);
                setError('Failed to load all quiz scores. Please check server connections and backend routes.');
                toast.error('Failed to load all quiz scores.');
            } finally {
                setLoading(false);
            }
        };

        fetchAllQuizScores();
    }, []);

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center vh-100">
                <p className="text-muted fs-5">Loading quiz scores...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div className="d-flex justify-content-center align-items-center vh-100">
                <div className="alert alert-danger text-center" role="alert">
                    <strong className="font-weight-bold">Error!</strong>
                    <span className="d-block d-sm-inline"> {error}</span>
                </div>
            </div>
        );
    }

    if (!quizScores || quizScores.length === 0) {
        return (
            <div className="d-flex flex-column justify-content-center align-items-center vh-100">
                <p className="text-muted fs-5">No quiz scores found.</p>
            </div>
        );
    }

    return (
        <div className="container-fluid py-4">
            <h2 className="text-dark mb-4">All Combined Quiz Scores</h2> {/* Updated title for clarity */}
            <div className="card shadow-sm">
                <div className="card-body p-0">
                    <div className="table-responsive">
                        <table className="table table-hover mb-0">
                            <thead className="bg-light">
                                <tr>
                                    <th scope="col" className="text-start text-uppercase text-muted small">Student Name</th>
                                    <th scope="col" className="text-start text-uppercase text-muted small">Student Email</th>
                                    <th scope="col" className="text-start text-uppercase text-muted small">Course Title</th>
                                    <th scope="col" className="text-start text-uppercase text-muted small">Quiz Title</th>
                                    <th scope="col" className="text-start text-uppercase text-muted small">Score</th>
                                    <th scope="col" className="text-start text-uppercase text-muted small">Max Score</th>
                                    <th scope="col" className="text-start text-uppercase text-muted small">Percentage</th>
                                    <th scope="col" className="text-start text-uppercase text-muted small">Date Taken</th>
                                </tr>
                            </thead>
                            <tbody>
                                {quizScores.map((score, index) => (
                                    <tr key={index}>
                                        <td className="align-middle">{score.studentName}</td>
                                        <td className="align-middle">{score.studentEmail}</td>
                                        <td className="align-middle">{score.courseTitle}</td>
                                        <td className="align-middle">{score.quizTitle}</td>
                                        <td className="align-middle">{score.score}</td>
                                        <td className="align-middle">{score.maxScore}</td>
                                        <td className="align-middle">
                                            {score.maxScore > 0 ? `${((score.score / score.maxScore) * 100).toFixed(2)}%` : 'N/A'}
                                        </td>
                                        <td className="align-middle">{new Date(score.dateTaken).toLocaleDateString()}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
            <ToastContainer position="top-right" autoClose={2000} />
        </div>
    );
};

export default QuizScoresList;
